import React, { useState, useEffect, useCallback } from 'react';
import TopMenu from '../../contexts/TopMenu';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import BankDetailsModal from './BankDetailsModal';
import PayoutModal from './PayoutModal';
import '../../styles/Ledger.css';
import { API_BASE_URL } from '../../config';

function Ledger({ email, onLogout }) {
  const [collections, setCollections] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ledgerData, setLedgerData] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [downloadingPdfs, setDownloadingPdfs] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalPages: 1,
    totalTransactions: 0
  });
  const [showOrderSearch, setShowOrderSearch] = useState(false);
  const [orderIdSearch, setOrderIdSearch] = useState('');
  const [activeFilters, setActiveFilters] = useState({
    payouts: false,
    gst: false,
    revenue: false,
    transactionType: 'all',
    startDate: '',
    endDate: '',
    orderId: ''
  });

  const getFilteredTransactions = useCallback((transactions) => {
    if (!transactions) return [];
    
    return transactions.filter(transaction => {
      if (activeFilters.payouts && !transaction.description.toLowerCase().includes('payout')) {
        return false;
      }
      
      if (activeFilters.gst && !transaction.description.toLowerCase().includes('gst')) {
        return false;
      }
      
      if (activeFilters.revenue && !transaction.description.toLowerCase().includes('revenue')) {
        return false;
      }
      
      if (activeFilters.transactionType !== 'all') {
        if (activeFilters.transactionType === 'credit' && transaction.type !== 'credit') {
          return false;
        }
        if (activeFilters.transactionType === 'debit' && transaction.type !== 'debit') {
          return false;
        }
      }
      
      // Date filtering
      if (activeFilters.startDate) {
        const transactionDate = dayjs(transaction.created_at);
        const startDate = dayjs(activeFilters.startDate);
        if (transactionDate.isBefore(startDate, 'day')) {
          return false;
        }
      }
      
      if (activeFilters.endDate) {
        const transactionDate = dayjs(transaction.created_at);
        const endDate = dayjs(activeFilters.endDate);
        // Use the end of day for the end date to include transactions from the entire end date
        const endOfDay = endDate.endOf('day');
        if (transactionDate.isAfter(endOfDay)) {
          return false;
        }
      }
      
      return true;
    });
  }, [activeFilters]);

  const toggleFilter = (filterType, value) => {
    setActiveFilters(prev => {
      if (filterType === 'transactionType' || filterType === 'startDate' || filterType === 'endDate' || filterType === 'orderId') {
        return { ...prev, [filterType]: value };
      }
      return { ...prev, [filterType]: !prev[filterType] };
    });
  };

  const clearDateFilter = () => {
    setActiveFilters(prev => ({
      ...prev,
      startDate: '',
      endDate: ''
    }));
  };

  const handleOrderSearch = (e) => {
    e.preventDefault();
    toggleFilter('orderId', orderIdSearch);
    setShowOrderSearch(false);
  };

  const prepareExportData = () => {
    if (!ledgerData?.transactions) return [];
    
    // Use the filtered transactions instead of all transactions
    const filteredTransactions = getFilteredTransactions(ledgerData.transactions);
    
    return filteredTransactions.map(transaction => ({
      Date: formatDate(transaction.created_at),
      Time: formatTime(transaction.created_at),
      Description: transaction.description,
      'Order Details': transaction.settlement_details ? 
        `Settlement Amount: ${formatAmount(transaction.settlement_details.amount)}${
          transaction.settlement_details.bank_details ? 
          `, Bank: ${transaction.settlement_details.bank_details.bankName}, Account: ${transaction.settlement_details.bank_details.accountNumber}, IFSC: ${transaction.settlement_details.bank_details.ifsc}` 
          : ''
        }`
        : transaction.metadata ? 
          Object.entries(transaction.metadata)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ')
          : '',
      Amount: `${transaction.type === 'credit' ? '+' : '-'} ${formatAmount(transaction.amount)}`
    }));
  };

  const exportToCSV = () => {
    const data = prepareExportData();
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Transactions");
    XLSX.writeFile(wb, `${selectedBrand.title}_transactions.csv`);
  };

  const exportToXLS = () => {
    const data = prepareExportData();
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Transactions");
    XLSX.writeFile(wb, `${selectedBrand.title}_transactions.xlsx`);
  };


  const downloadPdf = async (transaction) => {
    if (!transaction.ledger_request_id) return;
    
    // If download_url is present, redirect to that URL
    // if (transaction.download_url) {
    //   window.open(transaction.download_url, '_blank');
    //   return;
    // }
    
    // Otherwise, call the API to generate the PDF
    setDownloadingPdfs(prev => ({ ...prev, [transaction.ledger_request_id]: true }));
    
    try {
      const response = await fetch(`${API_BASE_URL}/brands/ledger/${transaction.ledger_request_id}/download`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: localStorage.getItem('userId'),
          auth_key: localStorage.getItem('authKey')
        })
      });
      
      const data = await response.json();
      
      if (data.status === 'success' && data.data?.download_url) {
        window.open(data.data.download_url, '_blank');
      } else {
        setError('Failed to generate PDF download. Please try again.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloadingPdfs(prev => ({ ...prev, [transaction.ledger_request_id]: false }));
    }
  };

  const fetchCollections = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/brands/accounts/get-collection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          auth_key: localStorage.getItem('authKey') || 'admin',
          user_id: localStorage.getItem('userId') || 'admin'
        }),
      });
      const data = await response.json();
      if (data.status === 200) {
        setCollections(data.data.collections);
      } else {
        setError(data.message || 'Failed to fetch collections');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLedgerData = useCallback(async (brandId, page = 1, pageSize = 10) => {
    if (!brandId) return;
    
    setLedgerLoading(true);
    try {
      // Build query parameters for filters
      const queryParams = new URLSearchParams({
        page: page,
        page_size: pageSize
      });
      
      // Prepare request body with filters
      const requestBody = {
        brand_id: brandId,
        filters: {
          payouts: activeFilters.payouts,
          gst: activeFilters.gst,
          revenue: activeFilters.revenue,
          transaction_type: activeFilters.transactionType,
          order_id: activeFilters.orderId || null,
          date_range: {
            start_date: activeFilters.startDate || null,
            end_date: activeFilters.endDate || null
          }
        }
      };
      
      const response = await fetch(`${API_BASE_URL}/brands/ledger?${queryParams.toString()}`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      
      const data = await response.json();
      if (data.status === 200) {
        setLedgerData(data.data);
        // Update pagination state from the response
        if (data.data.pagination) {
          setPagination({
            page: data.data.pagination.page,
            pageSize: data.data.pagination.page_size,
            totalPages: data.data.pagination.total_pages,
            totalTransactions: data.data.pagination.total_transactions
          });
        }
      } else {
        setError(data.message || 'Failed to fetch ledger data');
      }
    } catch (err) {
      setError(err.message);
      setLedgerData(null);
    } finally {
      setLedgerLoading(false);
    }
  }, [activeFilters]);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    fetchLedgerData(selectedBrand.brand_id, newPage, pagination.pageSize);
  };

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  useEffect(() => {
    if (selectedBrand) {
      // Reset to first page when brand changes
      fetchLedgerData(selectedBrand.brand_id, 1, pagination.pageSize);
    } else {
      setLedgerData(null);
    }
  }, [selectedBrand, fetchLedgerData, pagination.pageSize]);

  const formatDate = (dateString) => {
    return dayjs(dateString).format('MMM DD, YYYY');
  };

  const formatTime = (dateString) => {
    return dayjs(dateString).format('HH:mm:ss');
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Pagination component
  const PaginationControls = () => {
    return (
      <div className="pagination-controls">
        <button 
          onClick={() => handlePageChange(1)} 
          disabled={pagination.page === 1}
          className="pagination-button"
          title="First Page"
        >
          «
        </button>
        <button 
          onClick={() => handlePageChange(pagination.page - 1)} 
          disabled={pagination.page === 1}
          className="pagination-button"
          title="Previous Page"
        >
          ‹
        </button>
        
        <span className="pagination-info">
          Page {pagination.page} of {pagination.totalPages}
        </span>
        
        <button 
          onClick={() => handlePageChange(pagination.page + 1)} 
          disabled={pagination.page === pagination.totalPages}
          className="pagination-button"
          title="Next Page"
        >
          ›
        </button>
        <button 
          onClick={() => handlePageChange(pagination.totalPages)} 
          disabled={pagination.page === pagination.totalPages}
          className="pagination-button"
          title="Last Page"
        >
          »
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="dashboard-root">
        <TopMenu pageTitle="Ledger" email={email} onLogout={onLogout} />
        <div className="dashboard-container">
          <div className="dashboard-loading">
            <div className="loader"></div>
            <p>Loading your brands...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-root">
        <TopMenu pageTitle="Ledger" email={email} onLogout={onLogout} />
        <div className="dashboard-container">
          <div className="dashboard-error">
            <p>{error}</p>
            <button onClick={fetchCollections}>Retry</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-root">
      <TopMenu pageTitle="Ledger" email={email} onLogout={onLogout} />
      <div className="dashboard-container">
        <div className="ledger">
          <div className="brand-selector">
            <div className="brands-grid">
              {collections.map((collection) => (
                <div 
                  key={collection.handle}
                  className={`brand-card ${selectedBrand?.handle === collection.handle ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedBrand(selectedBrand?.handle === collection.handle ? null : collection);
                  }}
                  title={collection.description || collection.title}
                >
                  <div className="brand-image">
                    <img 
                      src={collection.image_url || '/placeholder-collection.png'} 
                      alt={collection.title}
                      onError={(e) => {
                        e.target.src = '/placeholder-collection.png';
                      }}
                    />
                  </div>
                  <div className="brand-name">{collection.title}</div>
                </div>
              ))}
            </div>
          </div>
          
          {selectedBrand ? (
            <div className="ledger-content">
              {ledgerLoading ? (
                <div className="dashboard-loading">
                  <div className="loader"></div>
                  <p>Loading ledger data...</p>
                </div>
              ) : ledgerData ? (
                <>
                  <div className="balance-section">
                    <div className="balance-card">
                      <div className="balance-label">Available Balance</div>
                      <div className="balance-amount">{formatAmount(ledgerData.balance)}</div>
                    </div>
                    <div className="balance-actions">
                      <button className="bank-button" onClick={() => setShowBankModal(true)}>
                        Bank
                      </button>
                      <button 
                        className="payout-button" 
                        onClick={() => setShowPayoutModal(true)}
                      >
                        Payout
                      </button>
                    </div>
                  </div>

                  <div className="transactions-section">
                    <div className="transactions-header">
                      <div className="transactions-title-wrapper">
                        <h2 className="transactions-title">
                          Transactions
                        </h2>
                        <button 
                          className="search-order-button" 
                          onClick={() => setShowOrderSearch(!showOrderSearch)}
                          title="Search by Order ID"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                          </svg>
                        </button>
                        {showOrderSearch && (
                          <form className="order-search-form" onSubmit={handleOrderSearch}>
                            <input
                              type="text"
                              value={orderIdSearch}
                              onChange={(e) => setOrderIdSearch(e.target.value)}
                              placeholder="Enter Order ID (e.g., ORD1743782002534)"
                              className="order-search-input"
                              autoFocus
                            />
                            <button 
                              type="submit" 
                              className="order-search-submit"
                              disabled={!orderIdSearch.trim()}
                            >
                              Search
                            </button>
                            <button 
                              type="button" 
                              className="order-search-cancel"
                              onClick={() => setShowOrderSearch(false)}
                            >
                              Cancel
                            </button>
                          </form>
                        )}
                      </div>
                      <div className="filters-container">
                        <button 
                          className={`filter-button ${activeFilters.payouts ? 'active' : ''}`}
                          onClick={() => toggleFilter('payouts')}
                        >
                          Payouts
                        </button>
                        <button 
                          className={`filter-button ${activeFilters.gst ? 'active' : ''}`}
                          onClick={() => toggleFilter('gst')}
                        >
                          GST
                        </button>
                        <button 
                          className={`filter-button ${activeFilters.revenue ? 'active' : ''}`}
                          onClick={() => toggleFilter('revenue')}
                        >
                          Revenue
                        </button>
                        <select 
                          className="filter-select"
                          value={activeFilters.transactionType}
                          onChange={(e) => toggleFilter('transactionType', e.target.value)}
                        >
                          <option value="all">All Transactions</option>
                          <option value="credit">Credit Only</option>
                          <option value="debit">Debit Only</option>
                        </select>
                        <div className="date-filter">
                          <input 
                            type="date" 
                            value={activeFilters.startDate} 
                            onChange={(e) => toggleFilter('startDate', e.target.value)}
                            aria-label="Start Date"
                            title="Filter by Start Date"
                            className="date-input"
                          />
                          <span className="date-separator">to</span>
                          <input 
                            type="date" 
                            value={activeFilters.endDate} 
                            onChange={(e) => toggleFilter('endDate', e.target.value)}
                            aria-label="End Date"
                            title="Filter by End Date"
                            className="date-input"
                            min={activeFilters.startDate} // Prevent selecting end date before start date
                          />
                          {(activeFilters.startDate || activeFilters.endDate) && (
                            <button 
                              className="clear-date-filter" 
                              onClick={clearDateFilter}
                              title="Clear Date Filter"
                              aria-label="Clear Date Filter"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="export-buttons">
                        <button onClick={exportToCSV}>Export to CSV</button>
                        <button onClick={exportToXLS}>Export to XLS</button>
                        {/* <button onClick={exportToPDF}>Export to PDF</button> */}
                      </div>
                    </div>
                    
                    {(() => {
                      return (
                        <>
                          <div className="filter-summary">
                            Showing {ledgerData.transactions?.length || 0} transactions of {pagination.totalTransactions} total
                            {(activeFilters.payouts || activeFilters.gst || activeFilters.revenue || 
                              activeFilters.transactionType !== 'all' || activeFilters.startDate || 
                              activeFilters.endDate || activeFilters.orderId) && (
                              <span className="filter-tags">
                                {activeFilters.payouts && <span className="filter-tag">Payouts</span>}
                                {activeFilters.gst && <span className="filter-tag">GST</span>}
                                {activeFilters.revenue && <span className="filter-tag">Revenue</span>}
                                {activeFilters.transactionType !== 'all' && (
                                  <span className="filter-tag">
                                    {activeFilters.transactionType === 'credit' ? 'Credits Only' : 'Debits Only'}
                                  </span>
                                )}
                                {activeFilters.orderId && (
                                  <span className="filter-tag order-id-tag">
                                    Order ID: {activeFilters.orderId}
                                    <button 
                                      className="filter-tag-remove" 
                                      onClick={() => toggleFilter('orderId', '')}
                                      aria-label="Remove order ID filter"
                                    >
                                      ×
                                    </button>
                                  </span>
                                )}
                                {(activeFilters.startDate || activeFilters.endDate) && (
                                  <span className="filter-tag">
                                    Date: {activeFilters.startDate ? formatDate(activeFilters.startDate) : 'Any'} 
                                    {' to '} 
                                    {activeFilters.endDate ? formatDate(activeFilters.endDate) : 'Any'}
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                          
                          <div className="table-container">
                            <table className="transactions-table">
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Description</th>
                                  <th>Transaction Information</th>
                                  <th>Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(() => {
                                  if (!ledgerData.transactions || ledgerData.transactions.length === 0) {
                                    return (
                                      <tr>
                                        <td colSpan="4" className="no-results">
                                          No transactions available.
                                        </td>
                                      </tr>
                                    );
                                  }
                                  
                                  return ledgerData.transactions.map((transaction) => (
                                    <tr key={transaction.id}>
                                      <td>
                                        <div className="transaction-date">
                                          {formatDate(transaction.created_at)}
                                        </div>
                                        <div className="transaction-metadata">
                                          {formatTime(transaction.created_at)}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="transaction-description">
                                          {transaction.description}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="transaction-metadata">
                                        {transaction.settlement_details ? (
                                          <div>
                                            <div className="metadata-item">
                                              <span className="metadata-label">Settlement Amount:</span>
                                              <span>{formatAmount(transaction.settlement_details.amount)}</span>
                                            </div>
                                            {transaction.settlement_details.bank_details && (
                                              <>
                                                <div className="metadata-item">
                                                  <span className="metadata-label">Bank:</span>
                                                  <span>{transaction.settlement_details.bank_details.bankName}</span>
                                                </div>
                                                <div className="metadata-item">
                                                  <span className="metadata-label">Account:</span>
                                                  <span>{transaction.settlement_details.bank_details.accountNumber}</span>
                                                </div>
                                                <div className="metadata-item">
                                                  <span className="metadata-label">IFSC:</span>
                                                  <span>{transaction.settlement_details.bank_details.ifsc}</span>
                                                </div>
                                              </>
                                            )}
                                          </div>
                                        ) : (
                                          transaction.metadata ? 
                                            Object.entries(transaction.metadata)
                                                .filter(([key]) => key !== 'ORDER ID')
                                                .map(([key, value]) => (
                                                  <div key={key} className="metadata-item">
                                                    <span className="metadata-label">{key.replace(/_/g, ' ')}:</span>
                                                    <span>{value}</span>
                                                  </div>
                                                ))
                                            : null
                                        )}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="amount-with-download">
                                          <div className={`transaction-amount ${transaction.type}`}>
                                            {transaction.type === 'credit' ? '+ ' : '- '}
                                            {formatAmount(transaction.amount)}
                                          </div>
                                          {transaction.ledger_request_id && (
                                            <button 
                                              className="pdf-download-button"
                                              onClick={() => downloadPdf(transaction)}
                                              disabled={downloadingPdfs[transaction.ledger_request_id]}
                                              title="Download PDF"
                                            >
                                              {downloadingPdfs[transaction.ledger_request_id] ? (
                                                <span className="download-spinner"></span>
                                              ) : (
                                                <svg 
                                                  className="download-icon" 
                                                  width="16" 
                                                  height="16" 
                                                  viewBox="0 0 24 24" 
                                                  fill="none" 
                                                  stroke="currentColor" 
                                                  strokeWidth="2" 
                                                  strokeLinecap="round" 
                                                  strokeLinejoin="round"
                                                >
                                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                                  <polyline points="14 2 14 8 20 8"></polyline>
                                                  <polyline points="12 18 12 12"></polyline>
                                                  <polyline points="9 15 12 18 15 15"></polyline>
                                                </svg>
                                              )}
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  ));
                                })()}
                              </tbody>
                            </table>
                          </div>
                          
                          {ledgerData.transactions && ledgerData.transactions.length > 0 && pagination.totalPages > 1 && (
                            <div className="pagination-container">
                              <PaginationControls />
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </>
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">📊</div>
                  <h3 className="empty-state-title">No Ledger Data Available</h3>
                  <p className="empty-state-description">
                    There are no transactions to display for this brand at the moment.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">👆</div>
              <h3 className="empty-state-title">Select a Brand</h3>
              <p className="empty-state-description">
                Choose a brand from above to view its ledger and transaction history.
              </p>
            </div>
          )}
        </div>
      </div>

      {showBankModal && (
        <BankDetailsModal
          isOpen={showBankModal}
          onClose={() => setShowBankModal(false)}
          brandId={selectedBrand?.brand_id}
          userId={localStorage.getItem('userId')}
          authKey={localStorage.getItem('authKey')}
        />
      )}

      {showPayoutModal && (
        <PayoutModal
          isOpen={showPayoutModal}
          onClose={() => setShowPayoutModal(false)}
          brandId={selectedBrand?.brand_id}
          userId={localStorage.getItem('userId')}
          authKey={localStorage.getItem('authKey')}
          onSuccess={() => {
            setShowPayoutModal(false);
            fetchLedgerData(selectedBrand?.brand_id, pagination.page, pagination.pageSize);
          }}
        />
      )}
    </div>
  );
}

export default Ledger; 